/*
   Copyright 2024, Guillermo Vega-Gorgojo

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/

import config from './data/config.json';
import dict from './data/dictionary.json';
import { updateHTMLtemplates } from './data/htmlTemplates.js';

import "leaflet/dist/leaflet.css";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'nouislider/dist/nouislider.css';
import 'shepherd.js/dist/css/shepherd.css';
import "./css/descifn.css";

import L from 'leaflet';
import bootstrap from "bootstrap/dist/js/bootstrap.bundle.min.js";
import JSONCrush from 'jsoncrush';

import { Mimapa, inicializarMapa, obtenerConfigMapaBase, actualizarMapaBase, mapaMovido, finActualizarMapa } from './modules/map.js';
import { cargarPaginaPrincipal, cargarBotonesMapa, actualizarIdiomaPanel } from './modules/mapControls.js';
import { CraftsAPI, TextEngine } from './modules/queryInterface.js';
import { getTaxonsInfo, getProvincesInfo, getMunicipalitiesInfo } from './modules/dataManager.js';
import { inicializarRegiones, cargarRegiones, ajustarPopupsRegiones } from './modules/regions.js';
import { inicializarSP, cargarSP, ajustarColorParcelas, ajustarPopupsParcelas } from './modules/plots.js';
import { activarEntradaLugares } from './modules/places.js';
import { visualizarFiltrosTaxon, correspondenciaTaxon } from './modules/taxons.js';
import { string2loc, loc2string, getLiteral, expandPrefix, applyPrefix, configurarModal } from './modules/util.js';
import { lanzarTour } from './modules/shepherd.js';
import { getFirebaseApp } from './modules/events.js';


// variables globales
let Sesion, Datos, Layers, Crafts, Solr, mimodal;

// vamos a la inicialización
inicializar();

// INICIALIZACIÓN APLICACIÓN
async function inicializar() {
	// genero plantillas HTML
	updateHTMLtemplates();
	
	// modal de carga
	configurarModal({ static: true, vertcent: true, nofooter: true, spinner: true,
		spinnerMessage: getLiteral(dict.confdata)}, getLiteral(dict.starting), null, null);
	mimodal = new bootstrap.Modal(document.getElementById('mimodal'));
	mimodal.show();
	
	// INICIALIZO Layers
	Layers = {};	
	
		
	// INICIALIZACIÓN SESIÓN
	Sesion = {};

	// inicializo timeouts
	Sesion.timeout = config.timeout;
	Sesion.huboTimeout = false;	

	// bloqueo para pintar mapa
	Sesion.actualizandoMapa = false; // bloqueo si se está actualizando el mapa
	Sesion.mapaMovido = false; // detecto si el mapa se movió para actualizar
	Sesion.idTimeoutActualizar = null; // id del timeout para actualización automática (para que no se bloquee)
	
	// no está dibujando un polígono
	Sesion.poligonero = false;
	
	// inicializo los índices de navegación de parcelas manual y bloqueadas
	Sesion.indMan = 0;
	Sesion.indBloq = 0;

	// estado de la sesión (construido a partir de la URL)
	Sesion.estado = {};		

	// inicialización zoom
	Sesion.zoom = undefined;
	Sesion.zoomUsados = {}; // guardo los zooms usados en el mapa para facilitar el cacheo
	
	// inicializo nombres científicos con el valor de nomci en local storage o true
	Sesion.nomci = localStorage.getItem('nomci') === "false"? false : true;
	
	// inicializo filtros de taxones
	Sesion.taxones = [];
	Sesion.taxonesColores = [];
	Sesion.taxonesFiltros = {}; // mapa, incluyendo el caso de treeUri
	Sesion.taxonAnd = false;
	//Sesion.taxonesUsados = {}; // guardo los taxones usados en el mapa para facilitar el cacheo
	// inicializo sugerencias taxones 
	Sesion.taxonFocus = -1;
	
	// progreso y estadísticos
	Sesion.infoCeldas = {}; // no hace falta inicializar, pero así queda claro su uso
	
	// inicializo las parcelas pintadas
	Sesion.parPintadas = {};
	Sesion.parPopupsRegenerar = {};
	Sesion.radioParPintadas = config.radioParcela; // 300 metros de radio por defecto
	
	// inicializo popup
	Sesion.popup = null;
	

	// INICIALIZACIÓN DATOS
	Datos = {};
	Datos.taxones = {};
	Datos.provs = {};
	Datos.munis = {};
	Datos.parcelas = {};
	Datos.parcelasDescarga = {}; // las parcelas que se descargan (tipos Plotifn2 y Plotifn3)	
	Datos.polis = []; // aquí guardo para cada polígono un objeto con las parcelas contenidas de cada edición de inventario
	Datos.poliplots = {}; // apaño para evitar recalcular las parcelas de los polígonos subidos por fichero (potencialmente complejos)
	Datos.statsProvs = {};
	Datos.statsMunis = {};	
	Datos.statsPolis = [];
	Datos.ladosCeldas = {}; // aquí calculo el lado de cada celda por nivel de zoom
	Datos.celdasParcelas = {}; // aquí incluyo las IRIs de las teselas en cada celda
	

	// CARGA PÁGINA PRINCIPAL
	cargarPaginaPrincipal();


	// INICIALIZACION SELECCIÓN PARCELAS
	Sesion.estado.SP = {};
	inicializarSP();
	
	
	// CARGA INICIAL DE LA URL
	// 1) carga localización si la hay
	// 2) carga taxones sin comprobar existencia
	// 3) ajusta vista
	// 4) no hace nada en el mapa (que no está aún creado)
	cargarURL(true);
				
	// SI NO HAY POSICIÓN INICIAL CARGO LA DE LA CONFIGURACIÓN
	if (!Sesion.estado.loc) { // precargo localización de la configuración
		Sesion.estado.loc = {
			lat: config.geolocstart[0],
			lng: config.geolocstart[1],
			z: config.zMin
		};
	}
	
	// INICIALIZO MAPA
	inicializarMapa();	
	
	// INCLUYO BOTONES DEL MAPA
	cargarBotonesMapa();	
	
	// INICIALIZACIÓN FIREBASE
	getFirebaseApp();	
	
	// INICIALIZACIÓN CRAFTS
	console.time("Configuración proveedor de datos");
	Crafts = new CraftsAPI(config.craftsConfig);
	Crafts.test()
		.then( () => {			
			// OK
			console.info("Proveedor de datos funciona");		
			console.timeEnd("Configuración proveedor de datos");
			
			// inicializo la caché sin esperar a que termine de indexar
			Crafts.initCache();
			
			// CONFIGURACIÓN SOLR
			configurarSolr();							
						
			// DETECCIÓN DE CAMBIOS EN EL MAPA
			Mimapa.on('moveend', mapaMovido);
			Sesion.listenerMoveend = true; // para ajustar localización si hay que inicializar a la posición del usuario
				
			// INICIALIZACIONES CRAFTS
			console.group("Inicialización de datos");
			
			// actualizo modal
			configurarModal({ static: true, vertcent: true, nofooter: true, spinner: true,
				spinnerMessage: getLiteral(dict.conftaxon)}, getLiteral(dict.starting), null, null);

			// obtengo datos de todos los taxones
			console.time("Carga de datos de taxones");
			getTaxonsInfo()
				.then(() => {
					console.info("Info de taxones cargada");
					console.timeEnd("Carga de datos de taxones");
					// habilito el botón de filtrar por taxón (desactivado hasta ahora)
					document.getElementById('bot_taxones').removeAttribute('disabled');
					
					// actualizo modal
					configurarModal({ static: true, vertcent: true, nofooter: true, spinner: true,
						spinnerMessage: getLiteral(dict.confprov)}, getLiteral(dict.starting), null, null);
					
					// obtengo datos de las provincias (espero a tener los datos de especies para hacerlo)
					console.time("Carga de datos de provincias");	
					getProvincesInfo()
						.then(() => {
							console.info("Info de provincias cargada");
							console.timeEnd("Carga de datos de provincias");
							
							// actualizo modal
							configurarModal({ static: true, vertcent: true, nofooter: true, spinner: true,
								spinnerMessage: getLiteral(dict.confmun)}, getLiteral(dict.starting), null, null);
						
							// obtengo datos de los municipios
							console.time("Carga de datos de municipios");
							getMunicipalitiesInfo()
								.then(() => {
									console.info("Info de municipios cargada");
									console.timeEnd("Carga de datos de municipios");
									console.groupEnd();
							
									// escondo el modal (si no está mostrado pongo un listener para cerrarlo)							
									mimodal.hide();
									document.getElementById('mimodal').addEventListener('shown.bs.modal', function () {
										mimodal.hide();
									}, { once: true });
							
									// cargo las regiones
									cargarRegiones();
									
									// AQUÍ SE HACE LA PRIMERA ACTUALIZACIÓN COMPLETA DEL MAPA
									cargarURL();									
								})
							.catch(error => console.error(error));
						})
						.catch(error => console.error(error));
				})
				.catch(error => console.error(error));	
		})
		.catch(error => {
			console.error(error);
			console.timeEnd("Configuración proveedor de datos");
			// aviso error
			errorProveedorDatosIrrecuperable(error);
		});
			
	
	// DETECCIÓN EVENTOS POPSTATE
	// https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onpopstate
	window.onpopstate = function(event) {
		// cargo la URL
		cargarURL();
	};

	
	// INICIALIZO LayerGroup DE PROVINCIAS/MUNICIPIOS
	Layers.regs = inicializarRegiones();
	// INICIALIZO LayerGroup DE POLÍGONOS
	Layers.polis = L.layerGroup().addTo(Mimapa);
	// INICIALIZO LayerGroup DE PARCELAS
	Layers.parcs = L.layerGroup().addTo(Mimapa);
		
	
	// INICIALIZACIÓN CUESTIONARIO
	// variables en localStorage
	// timestampPrimeraSesion: timestamp de la primera vez que utilizó el explorador (o que pulsó el botón más tarde)
	// cuestionarioNo: no mostrar más el cuestionario
	// si no había, guardo timestamp primera sesión
	if (!localStorage.getItem('timestampPrimeraSesion'))
		localStorage.setItem('timestampPrimeraSesion', Date.now());
	// detecto si debo mostrar el cuestionario en la sesión
	const gapinicio = Date.now() - Number(localStorage.getItem('timestampPrimeraSesion'));
	if (gapinicio > config.interSessionQGap && !localStorage.getItem('cuestionarioNo')) {
		Sesion.ponerAlertaCuestionario = true; // puede ponerse el cuestionario en la sesión
		Sesion.inicioSesion = Date.now(); // guardo inicio sesión
	}
	else
		Sesion.ponerAlertaCuestionario = false;
}


/////////////////////
// CONFIGURACIÓN SOLR
/////////////////////
function configurarSolr() {
	//console.group("Inicialización motor de texto");
	console.time("Configuración motor de texto");
	
	// inicializo Solr
	Solr = new TextEngine(config.solrConfig.path + config.solrConfig.suggestHandler,
		config.solrConfig.path + config.solrConfig.selectHandler);
	Solr.test()
		.then(() => {
			// OK
			console.info("Motor de texto funciona");						
			console.timeEnd("Configuración motor de texto");
			// muestro la entrada de los lugares
			activarEntradaLugares();
		})
		.catch(messageError => {			
			// log del error
			console.error(messageError);
			console.timeEnd("Configuración motor de texto");			
			// pongo Solr a null
			Solr = null;			
		});
}


////////////////////////
// ERROR PROVEEDOR DATOS
////////////////////////
function errorProveedorDatosIrrecuperable(messageError) {
	// ya no tiene sentido pedir datos
	Sesion.errordataprov = true;
	
	// configuro un modal para avisar de que no se puede explorar el inventario	
	configurarModal( { nofooter: true }, //{ vertcent: true, nofooter: true }, 
		getLiteral(dict.errorEndpointTitle), getLiteral(dict.errorEndpointText), null);
	
	// el modal ya se creó al inicializar la aplicación, por lo que no hace falta esta llamada:
	//mimodal = new bootstrap.Modal(document.getElementById('mimodal'));
		
	// si no está mostrado lo hago
	if (!document.querySelector('.modal.show'))
		mimodal.show();

	// quito temporizador
	finActualizarMapa();
}


///////////////////
// CARGA DE LA URL
// la función clave
///////////////////
async function cargarURL(inicial) {
	//console.warn(window.location);
	// 1) elimino alguna info del estado de la sesión
	delete Sesion.zoom; // no es estado, pero lo borro para evitar desajustes
	Sesion.taxones = [];
	

	// 2) actualizo el estado de la sesión a partir de la URL	
	// parseo del querystring de la URL
	const urlParams = new URLSearchParams(window.location.search);

	// actualizo localización si hay localización y está bien
	// en otro caso no borro la localización que hubiera
	const cadloc = urlParams.get('loc');
	if (cadloc != null) {
		const loc = string2loc(cadloc);
		if (loc != null) 
			Sesion.estado.loc = loc;
	}
	
	// extraigo la configuración del mapa de parámetros "mapType" y "mapDetails"
	// valores válidos los de config.mapConfig (y valor por defecto el 0)
	for (let mappar in config.mapConfig) {
		const val = urlParams.get(mappar);
		const valdef = config.mapConfig[mappar][0];
		Sesion.estado[mappar] = (val && config.mapConfig[mappar].includes(val))? val : valdef;
	}
		
	// extraigo versión del ifn
	if (urlParams.get('ifn') && config.ifnVersions.includes(urlParams.get('ifn')))
		Sesion.estado.ifn = urlParams.get('ifn');
	else // última versión de la configuración
		Sesion.estado.ifn = config.ifnVersions[config.ifnVersions.length-1];
	
	// extraigo lógica AND de los taxones
	if (urlParams.get('txand'))
		Sesion.taxonAnd = urlParams.get('txand') === 'true'? true : false;
	
	// extraigo SP si existe
	if (!inicial) {
		const cadsp = urlParams.get('sp');
		if (cadsp != null) {
			try {
				// recompongo la cadena del sp (con control de errores)
				const cadspext = JSONCrush.uncrush(decodeURIComponent(cadsp));
				if (cadspext !== encodeURIComponent(JSONCrush.crush(JSON.stringify(Sesion.estado.SP))))			
					await cargarSP(JSON.parse(cadspext));
			} catch(e) {
				console.error("Error del sp de la URL: " + e);
			}
		}
	}


	//3) ajusto vista según el estado cargado
					
	// actualizo título de la página
	document.title = "App - " + getLiteral(dict.title);
	document.querySelector("meta[property='og:title']").setAttribute('content', document.title);
	// ajuste propiedad meta de la url
	document.querySelector("meta[property='og:url']").setAttribute('content', window.location.href);
	
	// desbloqueo actualizaciones del mapa 
	Sesion.actualizandoMapa = false;	
	
	// ajusto radio con la versión del IFN
	document.getElementById(Sesion.estado.ifn).checked = true;
	
	// ajusto radio AND / OR de los taxones
	if (Sesion.taxonAnd)
		document.getElementById('and').checked = true;
	else
		document.getElementById('or').checked = true;
	
	// si no es la llamada inicial hago más cosas...
	if (!inicial) {
		// visualización filtros de taxón
		console.time("Visualización filtros taxón");
		visualizarFiltrosTaxon();
		console.timeEnd("Visualización filtros taxón");
		
		// actualización colores y popups parcelas
		const puris = Object.keys(Sesion.parPintadas);
		//console.time("Actualización color parcelas");
		ajustarColorParcelas(puris);
		//console.timeEnd("Actualización color parcelas");
		//console.time("Actualización popups parcelas");
		ajustarPopupsParcelas(puris);
		//console.timeEnd("Actualización popups parcelas");
		
		// actualización colores y popups provincias (ya no hace falta)
		//console.time("Actualización provs");
		//ajustarPopupsRegiones();
		//console.timeEnd("Actualización provs");
				
		// actualizo mapa base
		actualizarMapaBase();

		// centro el mapa en la localización de la sesión
		// esto dispara un evento de mapa movido, disparando su actualización en MODO MAPA
		Mimapa.setView([Sesion.estado.loc.lat, Sesion.estado.loc.lng], Sesion.estado.loc.z);

		// incluyo esto para que el mapa se reajuste
		// parece que así se resuelve el problema que había al pasar de recurso a mapa
		// tengo que poner esto después del setview, en otro caso no reacciona al setview
		Mimapa.invalidateSize();

		// tour inicial
		if (!localStorage.getItem('tourCompletado'))
			lanzarTour();
	}
}

//////////////////////////////////////////////
// GENERACIÓN DE LA URL A PARTIR DE LA SESIÓN
//////////////////////////////////////////////
function obtenerURL() { // devuelve la URL a partir de Sesion.estado
	// preparo la URL base con el path de Sesion.estado.path
	const { protocol, host } = window.location;
	let url = protocol + '//' + host + '/app';
	
	// preparo searchpars
	const searchpars = [];
	// localización
	searchpars.push('loc=' + loc2string(Sesion.estado.loc));
	// configuración de la versión del ifn
	searchpars.push('ifn='+Sesion.estado.ifn);
	// tipo de mapa
	searchpars.push('mapType='+Sesion.estado.mapType);	
	// detalles del mapa
	searchpars.push('mapDetails='+Sesion.estado.mapDetails);
	// lógica AND u OR de los taxones
	searchpars.push('txand='+Sesion.taxonAnd);
	// incluyo sp
	const cadsp = encodeURIComponent(JSONCrush.crush(JSON.stringify(Sesion.estado.SP)));
	searchpars.push('sp='+cadsp);
	/*console.log(Sesion.estado.SP);
	console.log(JSON.stringify(Sesion.estado.SP));
	const decsp = JSONCrush.uncrush(decodeURIComponent(sp));
	console.log(decsp);*/
	
	// incluyo los searchpars
	for (let i=0; i<searchpars.length; i++) {
		const sep = i==0? '?' : '&';
		url += sep + searchpars[i];
	}
	return url;
}

export { Sesion, Datos, Layers, Crafts, Solr, cargarURL, obtenerURL };